import { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import AuthContext from "common/store/AuthContext";
import StatusContext from "common/store/StatusContext";
import { REPORT_TYPE } from "common/types/consts/Defines";
import { useReport } from "./hooks/useReport";

import { ReportMaintenanceData } from "./types/ReportData";
import { getDateAndTimeString, toFormattedDate } from "utils/DateTools";
import { ShowEmpty } from "common/components/atoms/ShowEmpty";
import { Form, Table } from "react-bootstrap";
import ReportLockButton from "common/components/atoms/ReportLockButton";

type Props = {
  title: string;
  reportType: REPORT_TYPE;
  yearMonth: Date;
};

export const ReportListPanel = (props: Props) => {
  const { title, reportType, yearMonth } = props;
  const { getReports, setLock } = useReport();
  const { selectedPark } = useContext(AuthContext);
  const { setIsLoading, setLoadingMessage, setErrorMessage, setLockStatus } =
    useContext(StatusContext);
  const [reports, setReports] = useState<ReportMaintenanceData[]>([]);

  const [allChecked, setAllChecked] = useState(false);

  //--------------------------------------
  // 設定されている日付の日報データをロードする
  //--------------------------------------
  const loadReports = async () => {
    if (!selectedPark) return;

    setErrorMessage("");
    setIsLoading(true);
    setLoadingMessage("日報データの取得中");

    const { succeeded, msg, data } = await getReports(selectedPark.parkId, yearMonth, reportType);
    setIsLoading(false);
    if (!succeeded || !data) {
      setErrorMessage(msg || "");
      setReports([]);
      return;
    }
    setReports(data);
    setAllChecked(data.every((x) => x.isLocked));
  };

  const reportClicked = async (data: ReportMaintenanceData) => {
    if (!selectedPark) return;

    data.isLocked = !data.isLocked;

    const { succeeded, msg } = await setLock(selectedPark?.parkId, [data], reportType);
    if (!succeeded) {
      setErrorMessage(msg || "");
      data.isLocked = !data.isLocked;
      return;
    }
    setReports([...reports]);
    setAllChecked(reports.every((x) => x.isLocked));
    setLockStatus(undefined);
  };

  const lockAll = async (lock: boolean) => {
    if (!selectedPark) return;

    reports.forEach((x) => (x.isLocked = lock));
    const { succeeded, msg } = await setLock(selectedPark?.parkId, reports, reportType);
    if (!succeeded) {
      setErrorMessage(msg || "");
      // 途中まで成功している可能性もあるので初期化する
      loadReports();
      return;
    }
    setReports([...reports]);
    setAllChecked(lock);
    setLockStatus(undefined);
  };

  useEffect(() => {
    const f = async () => await loadReports();
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearMonth]);

  return (
    <div>
      <h4 className="mb-2">{title}</h4>
      {reports.length === 0 ? (
        <ShowEmpty>登録済みデータはありません</ShowEmpty>
      ) : (
        <STable bordered hover responsive>
          <thead className="text-center">
            <tr>
              <th>日付</th>
              <th>最終更新者</th>
              <th>最終更新日</th>
              <th>
                ロック
                <br />
                <Form.Check
                  checked={allChecked}
                  onChange={(e) => lockAll(e.currentTarget?.checked)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {reports.map((item) => (
              <tr key={`report_${item.reportDate}`}>
                <td>{toFormattedDate(new Date(item.reportDate))}</td>
                <td>{item.lastUpdated?.name}</td>
                <td>{item.lastUpdated && getDateAndTimeString(new Date(item.lastUpdated.date))}</td>
                <StdCenter>
                  <ReportLockButton isLocked={item.isLocked} onClick={() => reportClicked(item)} />
                </StdCenter>
              </tr>
            ))}
          </tbody>
        </STable>
      )}
    </div>
  );
};

const STable = styled(Table)`
  td {
    vertical-align: baseline;
  }
`;

const StdCenter = styled.td`
  text-align: center;
  vertical-align: middle;
`;
